import { fg } from '@atlaskit/platform-feature-flags';
import {
	isGraphActivity,
	type ActivityItemEdge,
} from '@atlassian/search-client/recent-activities/types';
import { type RecommendedObject } from '@atlassian/search-client/user-documents/types';

import type {
	ActivityEnrichment,
	EnrichedRecommendedObject,
	QuickFindResult,
} from '../../../../common/ui/quick-find/types';

/**
 * Enriches user documents (recommendations) with activity data from
 * the Activity Platform when the same document exists in the recent
 * activities data (matched by ID).
 *
 * The first matching activity per document is used. The rendering layer
 * decides how to use the enriched data (e.g. which timestamp to display).
 *
 * Returns a **new** array of QuickFindResult<EnrichedRecommendedObject>.
 * The original `userDocuments` array and its items are never mutated.
 */
export const enrichUserDocumentsWithActivity = (
	userDocuments: QuickFindResult<RecommendedObject>[],
	recentActivities: QuickFindResult<ActivityItemEdge>[],
): QuickFindResult<EnrichedRecommendedObject>[] => {
	if (
		userDocuments.length === 0 ||
		recentActivities.length === 0 ||
		!fg('qf_override_stale_recommendation_timestamp')
	) {
		return userDocuments;
	}

	// Build a lookup map: documentId → activity enrichment data
	const activityEnrichmentMap = new Map<string, ActivityEnrichment>();

	for (const activity of recentActivities) {
		const activityObject = activity.data.node.object;
		const documentId = isGraphActivity(activityObject) ? activityObject.data?.id : activity.id;

		if (!documentId) {
			continue;
		}

		const event = activity.data.node.event;

		const enrichment: ActivityEnrichment = {
			timestamp: event.timestamp,
			eventType: event.eventType,
		};

		// Use the first matching activity for each document
		if (!activityEnrichmentMap.has(documentId)) {
			activityEnrichmentMap.set(documentId, enrichment);
		}
	}

	return userDocuments.map((userDocument) => {
		const activityEnrichment = activityEnrichmentMap.get(userDocument.id);
		if (!activityEnrichment) {
			return userDocument;
		}

		return {
			...userDocument,
			data: {
				...userDocument.data,
				activityEnrichment,
			},
		};
	});
};
